// OpenTelemetry browser-to-Tempo distributed tracing
const OTLP_URL = "https://otel.linesystem.uz/v1/traces";

export function initTracer(serviceName) {
  if (typeof window === "undefined" || window.__otel_inited) return;
  window.__otel_inited = true;

  function randomHex(bytes) {
    const arr = new Uint8Array(bytes);
    if (window.crypto && window.crypto.getRandomValues) {
      window.crypto.getRandomValues(arr);
    } else {
      for (let i = 0; i < bytes; i++) arr[i] = Math.floor(Math.random() * 256);
    }
    return Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
  }

  function isTarget(url) {
    if (!url) return false;
    const str = String(url);
    if (str.includes("otel.linesystem.uz") || str.includes("sentry.linesystem.uz")) return false;
    return (
      str.startsWith("/") ||
      str.includes("erpline.uz") ||
      str.includes("crmline.uz") ||
      str.includes("hrline.uz") ||
      str.includes("linesystem.uz") ||
      str.includes("localhost")
    );
  }

  function parsePath(url) {
    try {
      const u = new URL(url, window.location.origin);
      return u.pathname;
    } catch {
      return String(url).split("?")[0];
    }
  }

  let queue = [];
  let timer = null;

  function flush() {
    if (queue.length === 0) return;
    const spans = queue.splice(0, queue.length);
    const payload = {
      resourceSpans: [
        {
          resource: {
            attributes: [{ key: "service.name", value: { stringValue: serviceName } }],
          },
          scopeSpans: [
            {
              scope: { name: "browser-tracer" },
              spans,
            },
          ],
        },
      ],
    };
    const body = JSON.stringify(payload);
    try {
      if (typeof fetch === "function") {
        fetch(OTLP_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body,
          mode: "cors",
          credentials: "omit",
          keepalive: true,
        }).catch(() => {});
      }
    } catch (_) {}
  }

  function schedule() {
    if (!timer) {
      timer = setTimeout(() => {
        timer = null;
        flush();
      }, 1000);
    }
  }

  window.addEventListener("beforeunload", flush);

  // Hook XMLHttpRequest (used by Axios)
  if (window.XMLHttpRequest) {
    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.__otel = { method: String(method).toUpperCase(), url: String(url) };
      return origOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      const meta = this.__otel;
      if (meta && isTarget(meta.url)) {
        const traceId = randomHex(16);
        const spanId = randomHex(8);
        const startNano = String(Date.now()) + "000000";
        meta.traceId = traceId;
        meta.spanId = spanId;
        meta.startNano = startNano;

        try {
          this.setRequestHeader("traceparent", `00-${traceId}-${spanId}-01`);
        } catch {}

        const onDone = () => {
          if (meta.done) return;
          meta.done = true;
          const endNano = String(Date.now()) + "000000";
          queue.push({
            traceId: meta.traceId,
            spanId: meta.spanId,
            name: `${meta.method} ${parsePath(meta.url)}`,
            kind: 3,
            startTimeUnixNano: meta.startNano,
            endTimeUnixNano: endNano,
            attributes: [
              { key: "http.method", value: { stringValue: meta.method } },
              { key: "http.url", value: { stringValue: meta.url } },
              { key: "http.status_code", value: { intValue: this.status || 0 } },
            ],
          });
          schedule();
        };

        this.addEventListener("loadend", onDone, { once: true });
        this.addEventListener("error", onDone, { once: true });
        this.addEventListener("abort", onDone, { once: true });
      }
      return origSend.apply(this, arguments);
    };
  }

  // Hook Fetch
  if (window.fetch) {
    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === "string" ? input : input?.url || "";
      if (!isTarget(url)) {
        return origFetch.apply(this, arguments);
      }
      const method = (init?.method || "GET").toUpperCase();
      const traceId = randomHex(16);
      const spanId = randomHex(8);
      const startNano = String(Date.now()) + "000000";

      const headers = new Headers(init?.headers || (typeof input === "object" ? input.headers : {}));
      headers.set("traceparent", `00-${traceId}-${spanId}-01`);

      const newInit = { ...init, headers };
      const promise = origFetch(input, newInit);

      const onDone = (status) => {
        const endNano = String(Date.now()) + "000000";
        queue.push({
          traceId,
          spanId,
          name: `${method} ${parsePath(url)}`,
          kind: 3,
          startTimeUnixNano: startNano,
          endTimeUnixNano: endNano,
          attributes: [
            { key: "http.method", value: { stringValue: method } },
            { key: "http.url", value: { stringValue: url } },
            { key: "http.status_code", value: { intValue: Number(status) || 0 } },
          ],
        });
        schedule();
      };

      return promise.then(
        (res) => {
          onDone(res.status);
          return res;
        },
        (err) => {
          onDone(0);
          throw err;
        }
      );
    };
  }
}
